import React, { MouseEvent, useState, useEffect, Dispatch, useCallback, useMemo } from 'react';
import '../../App.css';
import { IInjectInfo, IGameStatus, IPlayer, IHotwash, IMove } from '../../interfaces';
import { Player } from '../../Classes1';
import Modal from 'react-modal';
import { HostSelection } from '../../Classes1';
import { useAppDispatch } from '../../app/hooks'
import { updateHotwashSelection } from '../../features/hotwash/hotwashSlice'
import { isEmpty } from 'lodash';
import { shortRank } from '../rankMap';

export interface NodeProps {
    id: string;
    index: number;
    turn: number;
    injectItem: IInjectInfo;
    currentGame: IGameStatus;
    currentTurn: number;
    hotwashStatus: IHotwash;
    isHost: boolean;
    played: boolean;
    updateHotwashStatus: Dispatch<any>;
  }

  export interface Item {
    id: number
    text: string
  }

  export interface coaArr {
    id: number
    text: string
  }

  const customStyles = {
    content: {
      color: '#AAADBC',
      fontFamily: 'Source Sans 3',
      fontSize: '16px',
      background: '#323544',
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      padding: '16px',
      border: 'none',
      width: '808px',
      height: '636px',
      transform: 'translate(-50%, -50%)',
    }
  };

Modal.setAppElement('#root');

const HotwashNode: React.FC<NodeProps> = ({ id, turn, index, injectItem, currentGame, hotwashStatus, isHost, updateHotwashStatus, played }) => {

    let [isEditing, setIsEditing] = useState<string>('');
    const [nodeModalIsOpen, setNodeModalIsOpen] = useState<boolean>(false);
    const [tabCOA, setTabCOA] = useState<string>('selected');
    const [tabRFI, setTabRFI] = useState<string>('');
    const [tabInfo, setTabInfo] = useState<string>('');
    const [selectedPlayer, setSelectedPlayer] = useState<IPlayer>(new Player());
    const inputRef = React.useRef<any>()

    const dispatch: any = useAppDispatch();

    const node = useMemo(() => {
        return injectItem.nodes.find((n) => n.id === id);
    }, [injectItem, id]);

    const nodeMoves = useMemo(() => {
        return injectItem.moveHistory.filter((m) => m.type === 'action' && m.turn === node?.turn)
    }, [injectItem.moveHistory, node])

    const playerMoves = useMemo(() => {
        return nodeMoves.filter(
            (m) => m.player === selectedPlayer.userID
        )
    }, [selectedPlayer.userID, nodeMoves])

    const closeNodeModal = async (ev: MouseEvent) => {
        console.log("closeNodeModal")
        ev.stopPropagation();
        if (isHost) {
            let newHostSelection: any = {};
            newHostSelection.hotwashId = hotwashStatus.gameId;
            newHostSelection.hostSelection = new HostSelection();
            
            newHostSelection.hostSelection.page = '';
            newHostSelection.hostSelection.turn = hotwashStatus.currentSelection.turn;
            newHostSelection.hostSelection.inject = hotwashStatus.currentSelection.inject;
            newHostSelection.hostSelection.node = '';
            newHostSelection.hostSelection.nodeTab = '';
            newHostSelection.hostSelection.nodePlayerID = '';
            await dispatch(updateHotwashSelection(newHostSelection))
            updateHotwashStatus(hotwashStatus.gameId)
        }
        setNodeModalIsOpen(false);
    }

    const countVotes = useCallback((type: string, item: any) => {
        return nodeMoves.reduce<number>((votes, m) =>
            votes + ((m.options.choiceType === type) && (parseInt(m.choice, 10) === item.id) ? 1 : 0)
        , 0)
    }, [nodeMoves])

    // Update vote counts when node or player changes.
    useEffect(() => {
        if (node) {
            const playerCount = currentGame.players.length;
            node.coas.forEach((coa) => {
                coa.votes = countVotes('coa', coa)
                coa.votePercentage = Math.round((coa.votes / playerCount) * 100);
                coa.groupChoice = (coa.votes / currentGame.players.length) > 0.5;
            });
            node.rfis.forEach((rfi) => {
                rfi.votes = countVotes('rfi', rfi);
                rfi.votePercentage = Math.round((rfi.votes / playerCount) * 100);
                rfi.groupChoice = (rfi.votes / currentGame.players.length) > 0.5;
            });
        }
    }, [node, countVotes, currentGame.players])

    const showNode = useCallback(() => {
        setIsEditing('editing');
        setNodeModalIsOpen(true);
        if (!node) {
            return;
        }
        setSelectedPlayer(currentGame.players[0])
    }, [currentGame.players, node])

    const clickNode = async (ev: MouseEvent) => {
        ev.stopPropagation();

        if (!played) {
            return;
        }

        if (isHost) {
            let newHostSelection: any = {};
            newHostSelection.hotwashId = hotwashStatus.gameId;
            newHostSelection.hostSelection = new HostSelection();
            
            newHostSelection.hostSelection.page = '';
            newHostSelection.hostSelection.turn = hotwashStatus.currentSelection.turn;
            newHostSelection.hostSelection.inject = hotwashStatus.currentSelection.inject;
            newHostSelection.hostSelection.node = id;
            newHostSelection.hostSelection.nodeTab = '';
            newHostSelection.hostSelection.nodePlayerID = '';
            await dispatch(updateHotwashSelection(newHostSelection))
            updateHotwashStatus(hotwashStatus.gameId)
        }
        showNode();
    }

    const showPlayer = useCallback((userID: string | undefined) => {
        const player = currentGame.players.find((p) => p.userID === userID);
        if (player) {
            setSelectedPlayer(player);
        }
    }, [currentGame.players])

    const selectPlayer = async (ev: MouseEvent) => {
        console.log("selectPlayer")

        ev.stopPropagation();

        if (isHost) {
            const target = ev.currentTarget as HTMLElement;
            if (target) {
                let newHostSelection: any = {};
                newHostSelection.hotwashId = hotwashStatus.gameId;
                newHostSelection.hostSelection = new HostSelection();
                
                newHostSelection.hostSelection.page = '';
                newHostSelection.hostSelection.turn = hotwashStatus.currentSelection.turn;
                newHostSelection.hostSelection.inject = hotwashStatus.currentSelection.inject;
                newHostSelection.hostSelection.node = id;
                newHostSelection.hostSelection.nodeTab = '';
                newHostSelection.hostSelection.nodePlayerID = target.dataset.id;
                
                await dispatch(updateHotwashSelection(newHostSelection))
                updateHotwashStatus(hotwashStatus.gameId)

                showPlayer(target.dataset.id);
            }
        }
    }

    const showTab = useCallback((tabId: string | undefined) => {
        switch(tabId) {
            case 'coa':
                setTabCOA('selected')
                setTabRFI('')
                setTabInfo('')
              break;
            case 'rfi':
                setTabCOA('')
                setTabRFI('selected')
                setTabInfo('')
              break;
            case 'info':
                setTabCOA('')
                setTabRFI('')
                setTabInfo('selected')
              break;
            default:
              break;
          }
    }, []);

    const selectTab = (ev: MouseEvent) => {
        ev.stopPropagation();
        const target = ev.currentTarget as HTMLElement;
        
        if (target) {
            showTab(target.dataset.id)
        }
    }

    useEffect(() => {
        if (hotwashStatus.currentSelection.node === id && hotwashStatus.currentSelection.inject === injectItem.id) {
            // show node
            showNode()
        }

        if (hotwashStatus.currentSelection.nodePlayerID !== '') {
            showPlayer(hotwashStatus.currentSelection.nodePlayerID);
        }

        if (hotwashStatus.currentSelection.nodeTab !== '') {
            showTab(hotwashStatus.currentSelection.nodeTab);
        }
        
    }, [hotwashStatus, injectItem.id, id, showNode, showPlayer, showTab]);

    const expandDescription = (evt: MouseEvent) : void => {
        const element = evt.target as HTMLElement;
        if (element.offsetHeight < element.scrollHeight) {
            element.style.height = `${element.scrollHeight}px`;
        } else {
            element.style.height = 'auto';
        }
    }

    return (
      node &&
      <div className={`hotwash-node ${isEditing} ${!played ? 'unplayed' : ''}`} onClick={clickNode} ref={inputRef} data-id={id} data-turn={turn} key={index}>
            <div className="hotwash-node-name">{node.name}</div>
            <div className="hotwash-node-detail">
                <div>
                {
                    node.coas.length > 0 &&
                    <div className="hotwash-sm-branch">
                        <img src="img/small-branch.svg" alt="COAs" />
                    </div>
                }
                {
                    node.rfis.length > 0 &&
                    <div className="hotwash-sm-question">
                        <img src="img/small-question.svg" alt="RFIs" />
                    </div>
                }
                {
                    node.informationBriefs.length > 0 &&
                    <div className="hotwash-sm-clipboard">
                        <img src="img/small-clipboard.svg" alt="Info Briefs" />
                    </div>
                }
                </div>
            </div>

            <Modal
                isOpen={nodeModalIsOpen}
                onRequestClose={closeNodeModal}
                style={customStyles}
                contentLabel="Node Detail Modal"
            >
               <div className="node-modal-small-title">Inject Node</div>
               <div className="node-modal-title">{node.name}</div>
               <div onClick={ expandDescription } className="node-modal-description">
                    {node.description}
               </div>
               <div className="node-modal-tabs">
                    <div className={`node-modal-tab coa-tab-title ${tabCOA}`} data-id="coa" onClick={selectTab}>Course of Action</div>
                    <div className={`node-modal-tab rfi-tab-title ${tabRFI}`} data-id="rfi" onClick={selectTab}>Requests for Information</div>
                    <div className={`node-modal-tab info-tab-title ${tabInfo}`} data-id="info" onClick={selectTab}>Information Briefs</div>
                </div>
                    <div className="node-modal-tab-left">
                            <div className="node-modal-team-label">Team Members</div>
                            <div className="node-modal-team-content">
                                {currentGame.players.map((player: IPlayer, i: number) => {
                                    return <div className={`node-modal-team-member ${player.userID === selectedPlayer.userID ? 'selected' : ''}`} data-id={player.userID} onClick={selectPlayer} key={i}>
                                        <div className="member-name-title">
                                            <div className="member-title">{shortRank(player.rank)}</div>
                                            <div className="member-lastname">{player.lastName}</div>
                                        </div>
                                        <div className="member-role">
                                            {player.role}
                                        </div>
                                    </div>
                                })}
                        </div>
                    </div>
                    <div className="node-modal-tab-right">
                            <div className={`node-modal-tab-content coa-tab ${tabCOA}`}>
                                {node.coas.length === 0
                                ? <div className="node-modal-intent">
                                    <div className="node-modal-intent-title">&nbsp;</div>
                                    <div className="node-modal-info-content">This inject ends with this turn.</div>
                                </div>
                                : <>
                                <div className="node-modal-intent">
                                    <div className="node-modal-intent-title">Intent</div>
                                    {playerMoves.filter(
                                        (m) => m.options.choiceType === 'coa'
                                    ).map((move: IMove, i: number) =>
                                        <div className="node-modal-intent-content" key={move.id}>
                                                {isEmpty(move.comment) ? 'Only course of action accepted by default.' : move.comment}
                                            <div className="node-modal-intent-sig"> - {move.player}</div>
                                        </div>
                                    )}
                                    
                                </div>
                                <div className="node-modal-coa-coas">
                                    <div className="node-modal-intent-title">Courses of Action</div>
                                    <div className="node-modal-coa-coas-content">
                                    {node.coas.map((coa: any, i: number) => {
                                        const vote = playerMoves.find((m) => m.options.choiceType === 'coa' && coa.id === parseInt(m.choice,10))
                                        return <div className="node-modal-coas-item" key={coa.id}>
                                            <div className={`node-modal-coas-vote ${coa.groupChoice ? 'chosen' : ''}`}>
                                            {
                                                vote &&
                                                <img src="img/green-check-shadow.svg" alt="check"  className="vote-checked"></img>
                                            }
                                            </div>
                                            <div className="node-modal-coas-vote-desc">{coa.displayText}</div>
                                            <div className="node-modal-coas-vote-count">{coa.votes} Votes</div>
                                            <div className="node-motal-vote-progress-bar" style={{width: `${coa.votePercentage}%`}}></div>
                                        </div>
                                    })}
                                    </div>
                                </div>
                                </>}
                            </div>
                            <div className={`node-modal-tab-content rfi-tab ${tabRFI}`}>
                                {node.rfis.length === 0
                                ? <div className="node-modal-intent">
                                    <div className="node-modal-intent-title">&nbsp;</div>
                                    <div className="node-modal-info-content">This turn did not allow for Request For Information.</div>
                                </div>
                                : <>
                                <div className="node-modal-intent-title">Intent</div>
                                {playerMoves.filter(
                                    (move) => move.options.choiceType === 'rfi'
                                ).map((move) =>
                                    <div className="node-modal-intent-content" key={move.id}>
                                        {move.comment}
                                        <div className="node-modal-intent-sig"> - {move.player}</div>
                                    </div>
                                )}
                                
                                <div className="node-modal-coa-coas">
                                    <div className="node-modal-rfi-title">Requests For Information</div>
                                    <div className="node-modal-rfi-content">
                                    {node.rfis.map((rfi, i: number) => {
                                        const vote = playerMoves.find((m) => m.options.choiceType === 'rfi' && m.choice === rfi.id.toString());
                                        const answer = rfi.roleContent.find((a:any) => a.role === selectedPlayer.role || a.role === 'All')?.displayText;
                                        return <div className="node-modal-coas-item" key={rfi.id}>
                                            <div className={`node-modal-coas-vote ${rfi.groupChoice ? 'chosen' : ''}`}>
                                                {
                                                    vote &&
                                                    <img src="img/green-check-shadow.svg" alt="check"  className="vote-checked"></img>
                                                }
                                            </div>
                                            <div className="node-modal-rfi-vote-desc">{rfi.displayText} <span style={{opacity: rfi.groupChoice ? 1.0 : 0.5}}>{answer ? answer : ''}</span></div>
                                            <div className="node-modal-coas-vote-count">{rfi.votes} Votes</div>
                                            <div className="node-motal-vote-progress-bar" style={{width: `${rfi.votePercentage}%`}}></div>
                                        </div>
                                    })}
                                    </div>
                                </div>
                                </>}
                            </div>
                            <div className={`node-modal-tab-content info-tab ${tabInfo}`}>
                                {node.rfis.length === 0
                                ? <div className="node-modal-intent">
                                    <div className="node-modal-intent-title">&nbsp;</div>
                                    <div className="node-modal-info-content">This turn did not have any Information Briefs.</div>
                                </div>
                                : <>
                                <div className="node-modal-info-content">
                                    <div className="node-modal-intent-title">Briefs</div>
                                    {node.informationBriefs.map((rc) =>
                                    <div className="node-modal-info-item">
                                        <div className="node-modal-info-role">
                                            <div className="member-role" style={{left: '7px'}}>{rc.role || 'All'}</div>
                                        </div>
                                        <div className="node-modal-info-text">
                                            <div className="node-modal-info-detail">{rc.displayText}</div>
                                            <div className="node-modal-info-status">
                                                <div className="node-modal-info-status1"></div>
                                                <div className="node-modal-info-status1"></div>
                                            </div>
                                        </div>
                                    </div>
                                    )}
                                </div>
                                </>}
                            </div>
                        </div>
                        <div className="node-modal-done-button" onClick={closeNodeModal}>Done</div>
            </Modal>
        </div>
    )
}

export default HotwashNode;